import { ClickAway } from '@components/core-ui/ClickAway';
import { useState } from 'react';
import ErrorIcon from '@components/icons/FormError.svg';
import TimeIcon from '@components/icons/PageTime.svg';

import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import { buildTimeInputValueOnDayTimeChange } from './helpers/buildTimeInputValueOnDayTimeChange';
import { buildTimeInputValueOnHourChange } from './helpers/buildTimeInputValueOnHourChange';
import { buildTimeInputValueOnMinuteChange } from './helpers/buildTimeInputValueOnMinuteChange';
import { parseTimeInputValue } from './helpers/parseTimeInputValue';
import { TProps } from './types/TProps';

const formFieldTimeInputTriggerSizeClassName = 'h-10 pl-11 pr-4';

const formFieldTimeInputTriggerBaseClassName = [
  'input inline-flex max-w-full flex-[1_1_auto] cursor-pointer items-center rounded-[5px] border-none bg-[var(--color-form-field-bg,var(--color-surface-muted))]',
  formFieldTimeInputTriggerSizeClassName,
  'text-xs font-medium leading-3 !text-[var(--color-form-field-text,var(--color-text-muted))]',
  'hover:shadow-[0_0_0_1px_var(--color-form-field-focus-ring,var(--color-nav-active-bg))] hover:outline-none focus:shadow-[0_0_0_1px_var(--color-form-field-focus-ring,var(--color-nav-active-bg))] focus:outline-none',
  'disabled:!bg-[var(--color-form-field-disabled-bg)] disabled:!text-[var(--color-form-field-disabled-text)] disabled:!shadow-none disabled:!outline-none',
  'disabled:![-webkit-text-fill-color:var(--color-form-field-disabled-text)] disabled:!opacity-100',
  'disabled:placeholder:!text-[var(--color-form-field-disabled-placeholder)] disabled:placeholder:!opacity-100',
].join(' ');

const formFieldTimeInputOpenClassName =
  'shadow-[0_0_0_1px_var(--color-form-field-focus-ring,var(--color-nav-active-bg))] outline-none';

const timeInputPlaceholderClassName = [
  'placeholder block w-[55px] font-medium opacity-100',
  '!text-[var(--color-form-field-placeholder,var(--color-text-muted))]',
].join(' ');

const timeInputValueClassName = [
  'value block w-[55px] font-medium opacity-100',
  '!text-[var(--color-form-field-text,var(--color-text-muted))]',
].join(' ');

const timeInputIconClassName = 'left-[10px] right-auto h-6 w-6';

const timeInputModalClassName =
  'modal absolute top-[50px] z-[999] text-nowrap rounded-[5px] bg-[var(--color-surface)] p-2.5 px-[7px] text-xs font-medium border border-[var(--color-border)] shadow-[0_10px_15px_var(--color-shadow)] after:absolute after:-top-4 after:left-[15px] after:z-[-1] after:border-8 after:border-transparent after:border-b-[var(--color-surface)] after:content-[""]';

const timeInputInputsClassName = 'inputs flex';

const timeInputColumnClassName = 'block text-center';

const timeInputHoursClassName = `${timeInputColumnClassName} border-r border-[var(--color-border)]`;

const timeInputMinutesClassName = `${timeInputColumnClassName} border-r border-[var(--color-border)]`;

const timeInputPickerItemClassName = 'mx-[3px] my-[3px] block cursor-pointer p-[5px]';

const timeInputPickerItemSelectedClassName =
  'mx-[3px] my-[3px] block cursor-pointer rounded-[3px] bg-[color-mix(in_srgb,var(--color-accent)_45%,transparent)] p-[5px]';

const timeInputDisabledTextClassName = '!text-[var(--color-form-field-disabled-text)]';

const timeInputDisabledSurfaceClassName =
  '!bg-[var(--color-form-field-disabled-bg)] !text-[var(--color-form-field-disabled-text)] !shadow-none !outline-none';

const timeInputErrorClassName = '!shadow-[0_0_0_1px_var(--color-form-field-error)] !outline-none';

const hoursArray = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const minutesArray = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
const dayTimesArray = ['AM', 'PM'] as const;

export const TimeInput = ({
  label,
  onChange,
  value = '',
  required = false,
  disabled = false,
  error = false,
  className = 'form-time-input w-full',
}: TProps) => {
  const safeValue = value ?? '12:00';
  const [open, setOpen] = useState(false);
  const title = `${label}${required ? ' *' : ''}`;
  const { minutes: valueMinutes, displayHours: valueHours, dayTime: valueDayTime } = parseTimeInputValue(safeValue);

  const changeHours = (hours: string) => {
    onChange(buildTimeInputValueOnHourChange(safeValue, hours, valueDayTime));
  };

  const changeMinutes = (minutes: string) => {
    onChange(buildTimeInputValueOnMinuteChange(safeValue, minutes));
  };

  const changeDayTime = (daytime: (typeof dayTimesArray)[number]) => {
    onChange(buildTimeInputValueOnDayTimeChange(safeValue, valueHours, daytime));
  };

  const openModal = () => {
    if (!disabled && !open) {
      setOpen(true);
    }
  };

  const closeModal = () => {
    if (!disabled && open) {
      setOpen(false);
    }
  };

  const renderModal = () => {
    if (!open) {
      return null;
    }
    return (
      <ClickAway onClickAway={closeModal}>
        <span className={timeInputModalClassName}>
          <span className={timeInputInputsClassName}>
            <span className={timeInputHoursClassName}>
              {hoursArray.map((hour) => (
                <span
                  className={valueHours === hour ? timeInputPickerItemSelectedClassName : timeInputPickerItemClassName}
                  key={`hour-${hour}`}
                  onClick={() => changeHours(hour)}
                >
                  {hour}
                </span>
              ))}
            </span>
            <span className={timeInputMinutesClassName}>
              {minutesArray.map((minute) => (
                <span
                  className={
                    valueMinutes === minute ? timeInputPickerItemSelectedClassName : timeInputPickerItemClassName
                  }
                  key={`minute-${minute}`}
                  onClick={() => changeMinutes(minute)}
                >
                  {minute}
                </span>
              ))}
            </span>
            <span className={timeInputColumnClassName}>
              {dayTimesArray.map((daytime) => (
                <span
                  className={
                    valueDayTime === daytime ? timeInputPickerItemSelectedClassName : timeInputPickerItemClassName
                  }
                  key={`daytime-${daytime}`}
                  onClick={() => changeDayTime(daytime)}
                >
                  {daytime}
                </span>
              ))}
            </span>
          </span>
        </span>
      </ClickAway>
    );
  };

  const triggerClassName = [
    formFieldTimeInputTriggerBaseClassName,
    open ? formFieldTimeInputOpenClassName : '',
    error ? timeInputErrorClassName : '',
    disabled ? timeInputDisabledSurfaceClassName : '',
    disabled ? 'pointer-events-none' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FormFieldRoot disabled={disabled} className={className}>
      <FormFieldIcon className={timeInputIconClassName}>{error ? <ErrorIcon /> : <TimeIcon />}</FormFieldIcon>
      <span className={triggerClassName} onClick={openModal}>
        {!value ? (
          <span className={disabled ? timeInputDisabledTextClassName : timeInputPlaceholderClassName}>{title}</span>
        ) : (
          <span className={disabled ? timeInputDisabledTextClassName : timeInputValueClassName}>
            {valueHours}:{valueMinutes} {valueDayTime}
          </span>
        )}
      </span>
      {renderModal()}
    </FormFieldRoot>
  );
};
